import { useEffect, useMemo, useRef, useState } from "react";
import { api, API, eur, formatApiErrorDetail } from "@/lib/api";
import { MAR_ACCOUNTS, marName } from "@/lib/mar";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  ReceiptText, UploadCloud, CheckCircle2, Trash2, AlertTriangle, Plus, X,
  ZoomIn, ZoomOut, Maximize2, ExternalLink, Loader2, Search, RefreshCw,
} from "lucide-react";

const nlDate = (iso) => { try { const [y, m, d] = iso.split("-"); return `${d}/${m}/${y}`; } catch { return iso; } };

// Aankopen handelsgoederen per btw-tarief (spiegelt backend grootboek_for_invoice).
const GOODS_RATE = (rate) => {
  const r = Math.round(parseFloat(rate) || 0);
  const m = {
    0: ["604000", "Aankopen handelsgoederen 0%"],
    6: ["604006", "Aankopen handelsgoederen 6%"],
    12: ["604012", "Aankopen handelsgoederen 12%"],
    21: ["604021", "Aankopen handelsgoederen 21%"],
  };
  const [account, name] = m[r] || ["604000", "Aankopen handelsgoederen"];
  return { account, name };
};

// Gedeelde preview-cache (docId -> object-URL) zodat een reeds (voor)geladen
// factuur meteen zichtbaar is bij het openen van het reviewvenster (geen wit scherm).
const previewCache = new Map();
const previewPromises = new Map();
function fetchPreview(docId) {
  if (previewCache.has(docId)) return Promise.resolve(previewCache.get(docId));
  if (previewPromises.has(docId)) return previewPromises.get(docId);
  const token = localStorage.getItem("token");
  const p = fetch(`${API}/documents/${docId}/preview`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => { if (!r.ok) throw new Error("load"); return r.blob(); })
    .then((b) => { const u = URL.createObjectURL(b); previewCache.set(docId, u); previewPromises.delete(docId); return u; })
    .catch((e) => { previewPromises.delete(docId); throw e; });
  previewPromises.set(docId, p);
  return p;
}

function RO({ label, value, mono, testid }) {
  return (
    <div className="space-y-0.5">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-sm ${mono ? "font-mono" : ""} ${value ? "" : "text-muted-foreground"}`} data-testid={testid}>{value || "—"}</div>
    </div>
  );
}


function InvoicePreview({ docId, filename }) {
  const [url, setUrl] = useState(() => previewCache.get(docId) || null);
  const [err, setErr] = useState(false);
  const [scale, setScale] = useState(1);
  const isPdf = (filename || "").toLowerCase().endsWith(".pdf");
  useEffect(() => {
    const cached = previewCache.get(docId);
    if (cached) { setErr(false); setUrl(cached); setScale(1); return; }
    let active = true;
    setErr(false); setUrl(null); setScale(1);
    fetchPreview(docId)
      .then((u) => { if (active) setUrl(u); })
      .catch(() => { if (active) setErr(true); });
    return () => { active = false; };
  }, [docId]);
  const openFull = () => {
    const token = localStorage.getItem("token");
    fetch(`${API}/documents/${docId}/file`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => window.open(URL.createObjectURL(b), "_blank")).catch(() => {});
  };
  if (err) return (
    <div className="w-full h-full min-h-[300px] rounded-md border border-amber-200 bg-amber-50 flex flex-col items-center justify-center gap-2 p-6 text-center">
      <AlertTriangle className="h-8 w-8 text-amber-500" />
      <div className="font-medium text-amber-800">Bestand niet gevonden</div>
    </div>
  );
  if (!url) return (
    <div className="w-full h-full min-h-[300px] rounded-md bg-muted/40 border border-border flex flex-col items-center justify-center gap-3" data-testid="fact-preview-loading">
      <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
      <div className="text-sm text-muted-foreground">Factuur laden…</div>
    </div>
  );
  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center gap-1.5 mb-1.5 shrink-0">
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setScale((s) => Math.max(s - 0.25, 0.5))} data-testid="fact-zoom-out"><ZoomOut className="h-4 w-4" /></Button>
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setScale((s) => Math.min(s + 0.25, 4))} data-testid="fact-zoom-in"><ZoomIn className="h-4 w-4" /></Button>
        <Button variant="outline" size="sm" className="h-8" onClick={() => setScale(1)} data-testid="fact-zoom-reset"><Maximize2 className="h-3.5 w-3.5 mr-1.5" /> Passend</Button>
        <span className="text-xs text-muted-foreground tabular-nums ml-1">{Math.round(scale * 100)}%</span>
        <Button variant="ghost" size="sm" className="h-8 ml-auto" onClick={openFull} data-testid="fact-open-full"><ExternalLink className="h-3.5 w-3.5 mr-1.5" /> {isPdf ? "PDF in nieuw tabblad" : "Ware grootte"}</Button>
      </div>
      <div className="w-full flex-1 min-h-[300px] overflow-auto rounded-md border border-border bg-neutral-100 flex items-start justify-center">
        <img
          src={url}
          alt="Factuur"
          data-testid="fact-preview-img"
          onClick={() => setScale((s) => (s >= 2 ? 1 : Math.round((s + 0.25) * 100) / 100))}
          style={{ width: `${scale * 100}%` }}
          className="block max-w-none cursor-zoom-in"
        />
      </div>
    </div>
  );
}

const RATES = [0, 6, 12, 21];
const CARRIERS = ["GLS", "DPD", "BPOST", "PostNL", "Vinted Go", "Mondial Relay"];
const VEHICLES = [
  { label: "GLE personenwagen", plate: "2-GEV-356" },
  { label: "Vito lichte vracht", plate: "2-DUK-748" },
];
const LOCATIONS = ["Brustem", "De Leesberg"];
const ENERGY_TYPES = [
  { v: "elektriciteit", l: "Elektriciteit" },
  { v: "gas", l: "Aardgas" },
  { v: "beide", l: "Gas + elektriciteit" },
];
const BILL_TYPES = [
  { v: "voorschot", l: "Voorschot" },
  { v: "eindafrekening", l: "Eindafrekening" },
];
const AUTO_TYPES = [
  { v: "leasing", l: "Leasing/financiering" },
  { v: "onderhoud", l: "Onderhoud/service" },
  { v: "andere", l: "Overige autokosten" },
];

export default function Factuurboek() {
  const { canWrite } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [categories, setCategories] = useState([]);
  const [tab, setTab] = useState("pending");
  const [loading, setLoading] = useState(true);
  const fileInput = useRef(null);

  // upload progress
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, failed: 0 });
  const [failedFiles, setFailedFiles] = useState([]);   // File-objecten die na 3 pogingen faalden
  const cancelRef = useRef(false);

  // review
  const [review, setReview] = useState(null); // the invoice under review
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [advanced, setAdvanced] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [gbAccounts, setGbAccounts] = useState(null);
  const [payFilter, setPayFilter] = useState("all"); // all | paid | unpaid
  const [matchSrc, setMatchSrc] = useState("bnp");
  const [matchCands, setMatchCands] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchSel, setMatchSel] = useState(new Set());
  const [payBusy, setPayBusy] = useState(false);
  const [dubPay, setDubPay] = useState(null);
  const [dubBusy, setDubBusy] = useState("");

  useEffect(() => { api.get("/factuurboek/grootboek/rekeningen").then((r) => setGbAccounts(r.data)).catch(() => {}); }, []);

  const catLabel = (key) => categories.find((c) => c.key === key)?.label || key;

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/factuurboek/invoices"),
      api.get("/factuurboek/categories"),
    ]).then(([inv, cat]) => {
      setInvoices(inv.data.invoices);
      setPendingCount(inv.data.pending_count);
      setCategories(cat.data.categories);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const loadDubPay = () => {
    setDubPay(null);
    api.get("/factuurboek/duplicate-payments").then((r) => setDubPay(r.data)).catch(() => setDubPay({ items: [], count: 0 }));
  };
  useEffect(() => { if (tab === "dubpay") loadDubPay(); }, [tab]);
  const resolveDub = async (it) => {
    setDubBusy(it.invoice_id);
    try {
      await api.post("/factuurboek/duplicate-payments/resolve", {
        invoice_id: it.invoice_id, payment_id: it.dup_payment_id,
        relink_entry_id: it.bank_entry ? it.bank_entry.id : "" });
      toast.success("Dubbel opgelost — kunstmatige boeking verwijderd, echte bankregel gekoppeld");
      loadDubPay(); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setDubBusy(""); }
  };
  const resolveAllDub = async () => {
    setDubBusy("__all__");
    try {
      const r = await api.post("/factuurboek/duplicate-payments/resolve-all");
      toast.success(`${r.data.resolved} dubbel(s) opgelost${r.data.skipped ? ` · ${r.data.skipped} overgeslagen` : ""}`);
      loadDubPay(); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setDubBusy(""); }
  };

  // Previews van openstaande facturen op de achtergrond vooraf inladen -> gedeelde
  // blob-cache, zodat het reviewvenster daarna meteen (zonder wit scherm) opent.
  const prefetchedRef = useRef(new Set());
  useEffect(() => {
    const pend = invoices.filter((i) => i.status !== "confirmed" && i.doc_id && (i.filename || "").toLowerCase().endsWith(".pdf")).slice(0, 25);
    if (!pend.length) return;
    let cancelled = false;
    let idx = 0;
    const worker = async () => {
      while (!cancelled && idx < pend.length) {
        const inv = pend[idx++];
        if (prefetchedRef.current.has(inv.doc_id)) continue;
        prefetchedRef.current.add(inv.doc_id);
        try { await fetchPreview(inv.doc_id); } catch { /* stil */ }
      }
    };
    worker(); worker();  // concurrency 2, rustig voor de server
    return () => { cancelled = true; };
  }, [invoices]);

  const refreshCats = () => api.get("/factuurboek/categories").then((r) => setCategories(r.data.categories));

  const [clbBusy, setClbBusy] = useState(false);
  const restampClb = async () => {
    setClbBusy(true);
    try {
      const r = await api.post("/factuurboek/clb/restamp-supplier");
      toast.success(`${r.data.updated} CLB-factuur/-facturen op leverancierscode ${r.data.clb_account} gezet`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Herstel mislukt"); }
    finally { setClbBusy(false); }
  };

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (fileInput.current) fileInput.current.value = "";
    if (!files.length) return;
    if (files.length > 100 &&
        !window.confirm(`Je staat op het punt ${files.length} bestanden te uploaden. Dit gebeurt in kleine stukjes op de achtergrond en kan enkele minuten duren. Houd dit tabblad open. Doorgaan?`)) {
      return;
    }
    await runUpload(files);
  };

  // Robuuste bulk-upload: verwerkt willekeurig veel bestanden (bv. 800) in kleine stukken
  // met beperkte gelijktijdigheid + retry, zodat de server (beperkte CPU) niet vastloopt.
  const runUpload = async (files) => {
    setUploading(true);
    cancelRef.current = false;
    const CONCURRENCY = 4;     // max gelijktijdige uitlezingen
    const CHUNK = 40;          // verwerk in blokken; korte adempauze ertussen
    const CHUNK_PAUSE_MS = 800;
    setProgress({ done: 0, total: files.length, failed: 0 });
    let done = 0, failed = 0;
    const failedNow = [];

    const uploadOne = async (f) => {
      for (let attempt = 1; attempt <= 3; attempt++) {
        if (cancelRef.current) return false;
        const fd = new FormData();
        fd.append("file", f);
        try {
          await api.post("/factuurboek/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          return true;
        } catch (err) {
          if (attempt === 3) return false;
          await new Promise((r) => setTimeout(r, 1000 * attempt));  // backoff
        }
      }
      return false;
    };

    for (let start = 0; start < files.length; start += CHUNK) {
      if (cancelRef.current) break;
      const chunk = files.slice(start, start + CHUNK);
      let idx = 0;
      const worker = async () => {
        while (idx < chunk.length && !cancelRef.current) {
          const f = chunk[idx++];
          const ok = await uploadOne(f);
          if (!ok) { failed += 1; failedNow.push(f); }
          done += 1;
          setProgress({ done, total: files.length, failed });
        }
      };
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, chunk.length) }, worker));
      if (start + CHUNK < files.length && !cancelRef.current) {
        await new Promise((r) => setTimeout(r, CHUNK_PAUSE_MS));  // laat de server bijkomen
      }
    }

    setUploading(false);
    setFailedFiles(failedNow);
    const stopped = cancelRef.current;
    if (stopped) {
      toast.message(`Gestopt · ${done - failed} ingelezen, ${failed} mislukt (${files.length - done} niet verwerkt)`);
    } else if (failed) {
      toast.warning(`${done - failed} facturen ingelezen · ${failed} mislukt — klik "Probeer mislukte opnieuw"`);
    } else {
      toast.success(`${done} facturen ingelezen`);
    }
    setTab("pending");
    load();
  };

  const retryFailed = async () => {
    if (!failedFiles.length) return;
    const again = failedFiles;
    setFailedFiles([]);
    await runUpload(again);
  };

  const openReview = (inv) => { setReview(inv); setEdit({ ...inv, _origTotal: inv.total_incl, vat_lines: (inv.vat_lines || []).map((v) => ({ ...v })) }); setNewCat(""); setAdvanced(false); setMatchCands(null); setMatchSrc("bnp"); setGbEdit(null); };

  const PAY_SRC_LABEL = { bnp: "BNP Paribas Fortis", viva: "Viva", kasboek: "Kasboek (contant)", manueel: "Handmatig" };

  const searchBank = async (src) => {
    if (!edit) return;
    setMatchSrc(src); setMatchLoading(true); setMatchCands(null); setMatchSel(new Set());
    try {
      const r = await api.get(`/factuurboek/invoices/${edit.id}/match-bank`, { params: { source: src } });
      setMatchCands(r.data.candidates || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setMatchCands([]); }
    finally { setMatchLoading(false); }
  };

  const applyPayResult = (data) => {
    setEdit((e) => ({ ...e, ...data }));
    setInvoices((prev) => prev.map((i) => (i.id === edit.id ? { ...i, ...data } : i)));
  };

  const markPaid = async (source, entry_id) => {
    if (!edit) return;
    setPayBusy(true);
    try {
      const r = await api.post(`/factuurboek/invoices/${edit.id}/pay`, { source, entry_id: entry_id || null });
      applyPayResult(r.data);
      setMatchCands(null); setMatchSel(new Set());
      toast.success(r.data.paid ? "Volledig betaald ✓" : `Deelbetaling gekoppeld · nog ${eur(r.data.open_amount)} open`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setPayBusy(false); }
  };

  const linkSelected = async () => {
    if (!edit || matchSel.size === 0) return;
    setPayBusy(true);
    try {
      const payments = [...matchSel].map((id) => ({ source: matchSrc, entry_id: id }));
      const r = await api.post(`/factuurboek/invoices/${edit.id}/pay`, { payments });
      applyPayResult(r.data);
      setMatchCands(null); setMatchSel(new Set());
      toast.success(r.data.paid ? "Volledig betaald ✓" : `${payments.length} betaling(en) gekoppeld · nog ${eur(r.data.open_amount)} open`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setPayBusy(false); }
  };

  const markUnpaid = async (paymentId) => {
    if (!edit) return;
    setPayBusy(true);
    try {
      const r = await api.post(`/factuurboek/invoices/${edit.id}/unpay`, paymentId ? { payment_id: paymentId } : {});
      applyPayResult(r.data);
      toast.success(paymentId ? "Betaling ontkoppeld" : "Alle betalingen ontkoppeld");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setPayBusy(false); }
  };

  const setCreditNumber = (val) => setEdit((e) => ({ ...e, credit_note_number: val, is_credit_note: val.trim() ? true : e.is_credit_note }));

  const r2 = (n) => Math.round((parseFloat(n) || 0) * 100) / 100;
  const updateVatLines = (vl) => setEdit((e) => ({
    ...e, vat_lines: vl,
    total_incl: r2(vl.reduce((s, x) => s + (parseFloat(x.base) || 0) + (parseFloat(x.vat) || 0), 0)),
  }));
  const setLineRate = (i, val) => {
    const vl = edit.vat_lines.map((x) => ({ ...x }));
    const rate = parseInt(val, 10) || 0;
    vl[i].rate = rate; vl[i].vat = r2((parseFloat(vl[i].base) || 0) * rate / 100);
    updateVatLines(vl);
  };
  const setLineBase = (i, val) => {
    const vl = edit.vat_lines.map((x) => ({ ...x }));
    const rate = parseInt(vl[i].rate, 10) || 0;
    vl[i].base = val; vl[i].vat = r2((parseFloat(val) || 0) * rate / 100);
    updateVatLines(vl);
  };
  const setLineVat = (i, val) => {
    const vl = edit.vat_lines.map((x) => ({ ...x }));
    vl[i].vat = val;
    updateVatLines(vl);
  };
  const setLineIncl = (i, val) => {
    const vl = edit.vat_lines.map((x) => ({ ...x }));
    const rate = parseInt(vl[i].rate, 10) || 0;
    const incl = parseFloat(val) || 0;
    const base = r2(incl / (1 + rate / 100));
    vl[i].base = base; vl[i].vat = r2(incl - base);
    updateVatLines(vl);
  };
  const addVat = () => updateVatLines([...edit.vat_lines, { rate: 21, base: "", vat: "" }]);
  const removeVat = (i) => updateVatLines(edit.vat_lines.filter((_, idx) => idx !== i));

  const addCategory = async () => {
    if (!newCat.trim()) return;
    try {
      const r = await api.post("/factuurboek/categories", { label: newCat.trim() });
      await refreshCats();
      setEdit((e) => ({ ...e, category: r.data.key }));
      setNewCat("");
      toast.success("Categorie toegevoegd");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const savePatch = async () => {
    const body = {
      supplier_name: edit.supplier_name, supplier_vat: edit.supplier_vat, supplier_address: edit.supplier_address,
      invoice_number: edit.invoice_number,
      credit_note_number: edit.credit_note_number || "", is_credit_note: edit.is_credit_note,
      invoice_date: edit.invoice_date, category: edit.category, total_incl: parseFloat(edit.total_incl) || 0,
      note: edit.note || "",
      kind: edit.kind || "kost", carrier: edit.kind === "opbrengst" ? (edit.carrier || "") : "",
      vehicle: edit.category === "autokosten" ? (edit.vehicle || "") : "",
      vehicle_plate: edit.category === "autokosten" ? (edit.vehicle_plate || "") : "",
      auto_type: edit.category === "autokosten" ? (edit.auto_type || "") : "",
      location: edit.category === "nutsvoorzieningen" ? (edit.location || "") : "",
      energy_type: edit.category === "nutsvoorzieningen" ? (edit.energy_type || "") : "",
      energy_bill_type: edit.category === "nutsvoorzieningen" ? (edit.energy_bill_type || "") : "",
      gas_amount: edit.category === "nutsvoorzieningen" ? (parseFloat(edit.gas_amount) || 0) : 0,
      electricity_amount: edit.category === "nutsvoorzieningen" ? (parseFloat(edit.electricity_amount) || 0) : 0,
      is_bedrijfsmiddel: !!edit.is_bedrijfsmiddel,
      asset_category: edit.is_bedrijfsmiddel ? (edit.asset_category || "") : "",
      asset_term_years: edit.is_bedrijfsmiddel && edit.asset_term_years ? parseInt(edit.asset_term_years, 10) : null,
      commercial_discount: parseFloat(edit.commercial_discount) || 0,
      vat_lines: edit.vat_lines.map((v) => ({ rate: parseInt(v.rate, 10), base: parseFloat(v.base) || 0, vat: parseFloat(v.vat) || 0 })),
    };
    if (edit.clb) body.clb_blocks = _blockToPayload();
    return api.patch(`/factuurboek/invoices/${edit.id}`, body);
  };

  const saveOnly = async () => {
    setSaving(true);
    try { await savePatch(); toast.success("Opgeslagen"); setReview(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const confirm = async () => {
    setSaving(true);
    try {
      await savePatch();
      await api.post(`/factuurboek/invoices/${edit.id}/confirm`);
      toast.success("Factuur bevestigd en geboekt");
      // Lijst lokaal bijwerken (voelt instant) i.p.v. alles opnieuw ophalen
      setInvoices((prev) => prev.map((i) => i.id === edit.id
        ? { ...i, status: "confirmed", category: edit.category, supplier_name: edit.supplier_name ?? i.supplier_name }
        : i));
      setPendingCount((c) => Math.max(0, c - 1));
      setReview(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (inv, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Factuur van ${inv.supplier_name || "?"} verwijderen?`)) return;
    try { await api.delete(`/factuurboek/invoices/${inv.id}`); toast.success("Verwijderd"); if (review?.id === inv.id) setReview(null); load(); }
    catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
  };

  const dupeKey = (i) => `${i.supplier_id || i.supplier_vat || ""}||${(i.invoice_number || "").trim().toLowerCase()}`;
  const dupeMap = useMemo(() => {
    const m = {};
    for (const i of invoices) {
      if (!(i.invoice_number || "").trim()) continue;
      const k = dupeKey(i);
      (m[k] = m[k] || []).push(i.id);
    }
    return m;
  }, [invoices]);
  const isDupe = (i) => {
    const num = (i.invoice_number || "").trim();
    if (!num) return false;
    return (dupeMap[dupeKey(i)]?.length || 0) > 1;
  };

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = invoices.filter((i) => {
      if (tab === "pending") return i.status === "pending";
      if (tab === "confirmed") return i.status === "confirmed";
      if (tab === "dupe") return isDupe(i) && i.status !== "confirmed";
      return true; // "all"
    });
    if (tab === "dupe") {
      list = [...list].sort((a, b) =>
        (a.invoice_number || "").localeCompare(b.invoice_number || "") ||
        (a.supplier_name || "").localeCompare(b.supplier_name || ""));
    }
    if (q) {
      list = list.filter((i) => {
        const hay = [i.supplier_name, i.supplier_vat, i.invoice_number, i.credit_note_number,
          catLabel(i.category), String(i.total_incl ?? ""), String(i.signed_total ?? "")]
          .filter(Boolean).join(" ").toLowerCase();
        return hay.includes(q);
      });
    }
    if (payFilter === "paid") list = list.filter((i) => i.paid);
    else if (payFilter === "unpaid") list = list.filter((i) => !i.paid);
    return list;
  }, [invoices, tab, search, categories, dupeMap, payFilter]);
  const allCount = invoices.length;
  const confirmedCount = invoices.filter((i) => i.status === "confirmed").length;
  const dupeCount = invoices.filter((i) => isDupe(i) && i.status !== "confirmed").length;
  const openInvoices = useMemo(() => invoices.filter((i) => i.status === "confirmed" && i.kind !== "opbrengst" && !i.paid), [invoices]);
  const openTotal = useMemo(() => openInvoices.reduce((s, i) => s + Math.abs(parseFloat(i.signed_total ?? i.total_incl ?? 0) || 0), 0), [openInvoices]);

  // reset selection when switching tab or search
  useEffect(() => { setSelected(new Set()); }, [tab]);

  const shownIds = useMemo(() => shown.map((i) => i.id), [shown]);
  const allSelected = shownIds.length > 0 && shownIds.every((id) => selected.has(id));
  const toggleRow = (id, e) => {
    e?.stopPropagation();
    setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleAll = () => setSelected((prev) => {
    if (shownIds.every((id) => prev.has(id))) { const n = new Set(prev); shownIds.forEach((id) => n.delete(id)); return n; }
    return new Set([...prev, ...shownIds]);
  });
  const selectedInShown = shownIds.filter((id) => selected.has(id));
  const bulkConfirm = async () => {
    if (!selectedInShown.length) return;
    if (!window.confirm(`${selectedInShown.length} factuur(-en) bevestigen en boeken?`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/factuurboek/invoices/bulk-confirm", { ids: selectedInShown });
      toast.success(`${r.data.confirmed} bevestigd${r.data.errors ? `, ${r.data.errors} mislukt` : ""}`);
      setSelected(new Set()); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };
  const bulkDelete = async () => {
    if (!selectedInShown.length) return;
    if (!window.confirm(`${selectedInShown.length} factuur(-en) definitief verwijderen?`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/factuurboek/invoices/bulk-delete", { ids: selectedInShown });
      toast.success(`${r.data.deleted} verwijderd`);
      setSelected(new Set()); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };

  const _isDupeEdit = !!edit && isDupe(edit);
  const _linesIncl = edit ? r2((edit.vat_lines || []).reduce((s, x) => s + (parseFloat(x.base) || 0) + (parseFloat(x.vat) || 0), 0)) : 0;
  const _docTotal = edit ? r2(edit._origTotal) : 0;
  const _mismatch = !!edit && (edit.vat_lines || []).length > 0 && _docTotal > 0 && Math.abs(_linesIncl - _docTotal) > 0.05;
  const _showCredit = !!edit && (edit.credit_hint || edit.is_credit_note);

  // Live grootboekvoorstel (dubbele boeking) uit de huidige review-invoer.
  const debFirst = (lines) => {
    const d = lines.filter((l) => (l.debet || 0) > 0.0001);
    const c = lines.filter((l) => !((l.debet || 0) > 0.0001));
    return [...d, ...c];
  };
  const _grootboek = useMemo(() => {
    if (!edit) return [];
    if (edit.grootboek_override && edit.grootboek_override.length) {
      return debFirst(edit.grootboek_override.map((l) => ({ account: l.account || "", name: l.name || "",
        debet: r2(parseFloat(l.debet) || 0), credit: r2(parseFloat(l.credit) || 0) })));
    }
    if (!gbAccounts) return [];
    const sign = (edit.kind === "opbrengst") ? (edit.is_credit_note ? 1 : -1) : (edit.is_credit_note ? -1 : 1);
    const baseAbs = (edit.vat_lines || []).reduce((s, x) => s + Math.abs(parseFloat(x.base) || 0), 0);
    const vatAbs = (edit.vat_lines || []).reduce((s, x) => s + Math.abs(parseFloat(x.vat) || 0), 0);
    const totAbs = Math.abs(parseFloat(edit.total_incl) || r2(baseAbs + vatAbs));
    const base = r2(sign * baseAbs), vat = r2(sign * vatAbs), total = r2(sign * totAbs);
    const A = gbAccounts;
    const out = [];
    const push = (a, amt, side) => {
      let deb = 0, cred = 0;
      if (side === "D") { if (amt >= 0) deb = amt; else cred = -amt; }
      else { if (amt >= 0) cred = amt; else deb = -amt; }
      out.push({ account: a.account, name: a.name, debet: r2(deb), credit: r2(cred) });
    };
    if (edit.kind === "opbrengst") {
      push(A.revenue, base, "C");
      if (r2(vat) !== 0) push(A.vat_payable, vat, "C");
      push(A.customer, total, "D");
    } else {
      const sgn = total >= 0 ? 1 : -1;
      const vls = edit.vat_lines || [];
      const baseSum = r2(vls.reduce((s, x) => s + Math.abs(parseFloat(x.base) || 0), 0));
      const splitOk = !edit.is_bedrijfsmiddel && edit.category === "handelsgoederen"
        && vls.length > 0 && baseSum !== 0 && Math.abs(baseSum - Math.abs(base)) <= 0.02;
      if (edit.is_bedrijfsmiddel) {
        push(A.invest, base, "D");
      } else if (splitOk) {
        vls.forEach((vl) => {
          const b = Math.abs(parseFloat(vl.base) || 0);
          if (b === 0) return;
          push(GOODS_RATE(vl.rate), r2(sgn * b), "D");
        });
      } else {
        push(A.categories[edit.category] || A.default_cost, base, "D");
      }
      if (r2(vat) !== 0) push(A.vat_deduct, vat, "D");
      const sup = edit.supplier_account_code
        ? { account: edit.supplier_account_code,
            name: edit.supplier_account_name || `Leveranciers - ${edit.supplier_name || ""}`.trim() }
        : A.supplier;
      const korting = r2((base + vat) - total);
      if (Math.abs(korting) > 0.005 && A.discount) push(A.discount, korting, "C");
      push(sup, total, "C");
    }
    return debFirst(out);
  }, [edit, gbAccounts]);
  const _gbDeb = r2(_grootboek.reduce((s, l) => s + l.debet, 0));
  const _gbCred = r2(_grootboek.reduce((s, l) => s + l.credit, 0));

  const _isClb = !!(edit && edit.clb);
  const _clbBlocks = (edit && edit.grootboek_blocks) || [];
  const _isColruyt = !!edit && /colruyt/i.test(`${edit.supplier_name || ""} ${edit.supplier_vat || ""}`);
  const _showDiscount = !!edit && edit.kind !== "opbrengst" && !_isClb && (_isColruyt || r2(edit.commercial_discount) > 0);
  const _blockToPayload = () => {
    const p = {};
    ((edit && edit.grootboek_blocks) || []).forEach((b) => {
      if (b.key) p[b.key] = (b.lines || []).map((l) => ({
        account: (l.account || "").toString().trim(), name: (l.name || "").toString().trim(),
        debet: parseFloat(l.debet) || 0, credit: parseFloat(l.credit) || 0,
      }));
    });
    return p;
  };
  const setBlockLine = (bi, li, field, val) => setEdit((e) => {
    const blocks = e.grootboek_blocks.map((b) => ({ ...b, lines: b.lines.map((l) => ({ ...l })) }));
    blocks[bi].lines[li][field] = val;
    if (field === "account") { const nm = marName(val); if (nm) blocks[bi].lines[li].name = nm; }
    if (field === "debet" && (parseFloat(val) || 0) !== 0) blocks[bi].lines[li].credit = 0;
    if (field === "credit" && (parseFloat(val) || 0) !== 0) blocks[bi].lines[li].debet = 0;
    return { ...e, grootboek_blocks: blocks };
  });
  const addBlockLine = (bi) => setEdit((e) => {
    const blocks = e.grootboek_blocks.map((b) => ({ ...b, lines: b.lines.map((l) => ({ ...l })) }));
    blocks[bi].lines.push({ account: "", name: "", debet: 0, credit: 0 });
    return { ...e, grootboek_blocks: blocks };
  });
  const removeBlockLine = (bi, li) => setEdit((e) => {
    const blocks = e.grootboek_blocks.map((b) => ({ ...b, lines: b.lines.filter((_, i) => i !== li) }));
    return { ...e, grootboek_blocks: blocks };
  });

  const [gbEdit, setGbEdit] = useState(null); // null = niet aan het bewerken; array = bewerkregels
  const startGbEdit = () => setGbEdit(_grootboek.map((l) => ({ ...l })));
  const cancelGbEdit = () => setGbEdit(null);
  const setGbLine = (i, field, val) => setGbEdit((rows) => {
    const r = rows.map((l) => ({ ...l }));
    r[i][field] = val;
    if (field === "account") { const nm = marName(val); if (nm) r[i].name = nm; }
    if (field === "debet" && (parseFloat(val) || 0) !== 0) r[i].credit = 0;
    if (field === "credit" && (parseFloat(val) || 0) !== 0) r[i].debet = 0;
    return r;
  });
  const addGbLine = () => setGbEdit((rows) => [...rows, { account: "", name: "", debet: 0, credit: 0 }]);
  const removeGbLine = (i) => setGbEdit((rows) => rows.filter((_, idx) => idx !== i));
  const saveGbOverride = async () => {
    const payload = gbEdit.map((l) => ({ account: (l.account || "").toString().trim(), name: (l.name || "").toString().trim(),
      debet: parseFloat(l.debet) || 0, credit: parseFloat(l.credit) || 0 }));
    try {
      const r = await api.patch(`/factuurboek/invoices/${edit.id}`, { grootboek_override: payload });
      setEdit((e) => ({ ...e, grootboek_override: r.data.grootboek_override || payload, grootboek: r.data.grootboek || [] }));
      setGbEdit(null);
      toast.success("Boeking aangepast");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const resetGbOverride = async () => {
    try {
      const r = await api.patch(`/factuurboek/invoices/${edit.id}`, { grootboek_override: [] });
      setEdit((e) => ({ ...e, grootboek_override: null, grootboek: r.data.grootboek || [] }));
      setGbEdit(null);
      toast.success("Terug naar automatisch voorstel");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const _gbEditDeb = gbEdit ? r2(gbEdit.reduce((s, l) => s + (parseFloat(l.debet) || 0), 0)) : 0;
  const _gbEditCred = gbEdit ? r2(gbEdit.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0)) : 0;

  const setClbPaid = async (paid) => {
    setEdit((e) => ({ ...e, clb_paid: paid }));
    try {
      const r = await api.patch(`/factuurboek/invoices/${edit.id}`, { clb_paid: paid, clb_blocks: _blockToPayload() });
      setEdit((e) => ({ ...e, clb_paid: paid, grootboek_blocks: r.data.grootboek_blocks || [], grootboek: r.data.grootboek || [] }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="factuurboek-page">
      <datalist id="mar-accounts">
        {MAR_ACCOUNTS.map((a) => <option key={a.code} value={a.code} label={`${a.code} · ${a.name}`} />)}
      </datalist>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight flex items-center gap-2">
            <ReceiptText className="h-7 w-7 text-indigo-600" /> Factuurboek
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Aankoopfacturen in bulk uploaden, uitlezen en bevestigen.</p>
        </div>
        <div>
          <input ref={fileInput} type="file" multiple accept="application/pdf,image/*" className="hidden" data-testid="factuur-file-input" onChange={onPickFiles} />
          {failedFiles.length > 0 && !uploading && (
            <Button variant="outline" className="mr-2 border-amber-300 text-amber-700" disabled={!canWrite} data-testid="factuur-retry-failed-btn" onClick={retryFailed}>
              <RefreshCw className="h-4 w-4 mr-2" /> Probeer mislukte opnieuw ({failedFiles.length})
            </Button>
          )}
          <Button variant="outline" className="mr-2" disabled={!canWrite || clbBusy || uploading} data-testid="clb-restamp-btn" onClick={restampClb}>
            {clbBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Herstel CLB-leverancierscode
          </Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700" disabled={!canWrite || uploading} data-testid="factuur-upload-btn" onClick={() => fileInput.current?.click()}>
            <UploadCloud className="h-4 w-4 mr-2" /> Facturen uploaden
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="factuur-openstaand">
        <button className="text-left rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 transition-colors hover:bg-amber-100"
          data-testid="openstaand-total" onClick={() => { setTab("confirmed"); setPayFilter("unpaid"); }}>
          <div className="text-xs font-medium text-amber-700">Nog te betalen</div>
          <div className="text-2xl font-semibold tabular-nums text-amber-900" data-testid="openstaand-bedrag">{eur(openTotal)}</div>
        </button>
        <button className="text-left rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 transition-colors hover:bg-slate-100"
          data-testid="openstaand-count-card" onClick={() => { setTab("confirmed"); setPayFilter("unpaid"); }}>
          <div className="text-xs font-medium text-slate-600">Openstaande facturen</div>
          <div className="text-2xl font-semibold tabular-nums text-slate-800" data-testid="openstaand-count">{openInvoices.length}</div>
        </button>
      </div>


      {uploading && (
        <Card className="border-indigo-200" data-testid="factuur-progress">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 text-sm">
              <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
              <span className="flex-1">Bezig met uitlezen… {progress.done}/{progress.total}{progress.failed ? ` (${progress.failed} mislukt)` : ""}</span>
              <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 h-7" data-testid="factuur-upload-stop-btn" onClick={() => { cancelRef.current = true; }}>
                Stop
              </Button>
            </div>
            <div className="mt-2 h-2 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-indigo-600 transition-all" style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }} />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">Grote uploads worden in kleine stukjes verwerkt — houd dit tabblad open tot het klaar is.</p>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <Button variant={tab === "all" ? "default" : "outline"} className={tab === "all" ? "bg-indigo-600 hover:bg-indigo-700" : ""} data-testid="tab-all" onClick={() => setTab("all")}>
          Alle ({allCount})
        </Button>
        <Button variant={tab === "pending" ? "default" : "outline"} className={tab === "pending" ? "bg-indigo-600 hover:bg-indigo-700" : ""} data-testid="tab-pending" onClick={() => setTab("pending")}>
          Te bevestigen {pendingCount > 0 && <Badge className="ml-2 bg-white text-indigo-700">{pendingCount}</Badge>}
        </Button>
        <Button variant={tab === "dupe" ? "default" : "outline"} className={tab === "dupe" ? "bg-red-600 hover:bg-red-700 text-white" : "border-red-200 text-red-700"} data-testid="tab-dupe" onClick={() => setTab("dupe")}>
          Dubbel {dupeCount > 0 && <Badge className={`ml-2 ${tab === "dupe" ? "bg-white text-red-700" : "bg-red-600 text-white"}`}>{dupeCount}</Badge>}
        </Button>
        <Button variant={tab === "dubpay" ? "default" : "outline"} className={tab === "dubpay" ? "bg-red-600 hover:bg-red-700 text-white" : "border-red-200 text-red-700"} data-testid="tab-dubpay" onClick={() => setTab("dubpay")}>
          Dubbele betalingen {dubPay && dubPay.count > 0 && <Badge className={`ml-2 ${tab === "dubpay" ? "bg-white text-red-700" : "bg-red-600 text-white"}`}>{dubPay.count}</Badge>}
        </Button>
        <Button variant={tab === "confirmed" ? "default" : "outline"} className={tab === "confirmed" ? "bg-indigo-600 hover:bg-indigo-700" : ""} data-testid="tab-confirmed" onClick={() => setTab("confirmed")}>
          Bevestigd ({confirmedCount})
        </Button>
        <div className="relative ml-auto w-full sm:w-72">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-8" placeholder="Zoek op leverancier, nummer, bedrag…" data-testid="factuur-search" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      {tab !== "dubpay" && (
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-medium text-muted-foreground">Betaalstatus:</span>
        <Button size="sm" variant={payFilter === "all" ? "default" : "outline"} className={payFilter === "all" ? "bg-slate-700 hover:bg-slate-800" : ""} data-testid="pay-filter-all" onClick={() => setPayFilter("all")}>Alles</Button>
        <Button size="sm" variant={payFilter === "paid" ? "default" : "outline"} className={payFilter === "paid" ? "bg-emerald-600 hover:bg-emerald-700" : "text-emerald-700 border-emerald-300"} data-testid="pay-filter-paid" onClick={() => setPayFilter("paid")}>Betaald ✓</Button>
        <Button size="sm" variant={payFilter === "unpaid" ? "default" : "outline"} className={payFilter === "unpaid" ? "bg-amber-600 hover:bg-amber-700" : "text-amber-700 border-amber-300"} data-testid="pay-filter-unpaid" onClick={() => setPayFilter("unpaid")}>Niet betaald</Button>
      </div>
      )}

      {selectedInShown.length > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2.5" data-testid="bulk-bar">
          <span className="text-sm font-medium text-indigo-900" data-testid="bulk-count">{selectedInShown.length} geselecteerd</span>
          <div className="ml-auto flex items-center gap-2">
            {tab !== "confirmed" && canWrite && (
              <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" disabled={bulkBusy} data-testid="bulk-confirm" onClick={bulkConfirm}>
                <CheckCircle2 className="h-4 w-4 mr-1.5" /> Bevestig geselecteerde
              </Button>
            )}
            {canWrite && (
              <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" disabled={bulkBusy} data-testid="bulk-delete" onClick={bulkDelete}>
                <Trash2 className="h-4 w-4 mr-1.5" /> Verwijder geselecteerde
              </Button>
            )}
            <Button size="sm" variant="ghost" data-testid="bulk-clear" onClick={() => setSelected(new Set())}>Wissen</Button>
          </div>
        </div>
      )}

      {tab !== "dubpay" && (
      <Card className="shadow-sm">
        <CardContent className="p-0">
          {loading ? <div className="py-16 text-center text-muted-foreground">Laden…</div> : (
            <Table>
              <TableHeader><TableRow>
                <TableHead className="w-10">
                  <input type="checkbox" className="h-4 w-4 cursor-pointer align-middle" data-testid="select-all" checked={allSelected} onChange={toggleAll} />
                </TableHead>
                <TableHead>Leverancier</TableHead><TableHead>Factuurnr.</TableHead><TableHead>Datum</TableHead>
                <TableHead>Categorie</TableHead><TableHead className="text-right">Totaal</TableHead>
                <TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {shown.map((inv) => (
                  <TableRow key={inv.id} data-testid={`factuur-row-${inv.id}`} className={`cursor-pointer ${selected.has(inv.id) ? "bg-indigo-50/60" : ""}`} onClick={() => openReview(inv)}>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" className="h-4 w-4 cursor-pointer align-middle" data-testid={`select-${inv.id}`} checked={selected.has(inv.id)} onChange={(e) => toggleRow(inv.id, e)} />
                    </TableCell>
                    <TableCell className="font-medium">{inv.supplier_name || <span className="text-muted-foreground">Onbekend</span>}
                      {inv.supplier_vat ? <span className="text-xs text-muted-foreground block">{inv.supplier_vat}</span> : null}</TableCell>
                    <TableCell className="font-mono text-sm">{inv.invoice_number || "—"}</TableCell>
                    <TableCell className="text-sm">{inv.invoice_date ? nlDate(inv.invoice_date) : "—"}</TableCell>
                    <TableCell className="text-sm">{catLabel(inv.category)}</TableCell>
                    <TableCell className={`text-right tabular-nums font-medium ${inv.is_credit_note ? "text-red-600" : ""}`}>{eur(inv.signed_total)}</TableCell>
                    <TableCell>
                      {(tab === "all" || tab === "dupe") && (
                        inv.status === "confirmed"
                          ? <Badge variant="outline" className="text-emerald-700 border-emerald-300 mr-1">Bevestigd</Badge>
                          : <Badge variant="outline" className="text-indigo-700 border-indigo-300 mr-1">Te bevestigen</Badge>
                      )}
                      {inv.kind !== "opbrengst" && (
                        inv.paid
                          ? <Badge className="bg-emerald-600 text-white mr-1" data-testid={`paid-badge-${inv.id}`}>Betaald ✓</Badge>
                          : (inv.status === "confirmed" ? <Badge variant="outline" className="text-amber-700 border-amber-300 mr-1" data-testid={`unpaid-badge-${inv.id}`}>Niet betaald</Badge> : null)
                      )}
                      {inv.is_credit_note && <Badge variant="outline" className="text-red-600 border-red-300 mr-1">Creditnota</Badge>}
                      {inv.kind === "opbrengst" && <Badge className="bg-emerald-600 text-white mr-1" data-testid={`opbrengst-badge-${inv.id}`}>Opbrengst</Badge>}
                      {inv.energy_bill_type && <Badge variant="outline" className="text-sky-700 border-sky-300 mr-1" data-testid={`billtype-badge-${inv.id}`}>{inv.energy_bill_type === "voorschot" ? "Voorschot" : "Eindafrekening"}</Badge>}
                      {inv.reconciled === false && <Badge variant="outline" className="text-amber-700 border-amber-300 mr-1">Controleer btw</Badge>}
                      {isDupe(inv) && <Badge className="bg-red-600 text-white mr-1" data-testid={`dupe-badge-${inv.id}`}>Dubbel</Badge>}
                      {inv.extraction_method === "ocr" && <Badge variant="outline" className="text-muted-foreground mr-1">OCR</Badge>}
                      {inv.extraction_method === "failed" && <Badge variant="outline" className="text-red-600 border-red-300">Niet gelezen</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      {canWrite && (
                        <Button variant="ghost" size="icon" data-testid={`factuur-del-${inv.id}`} onClick={(e) => del(inv, e)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {shown.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">
                    {search ? "Geen resultaten voor deze zoekopdracht." :
                      tab === "pending" ? "Geen facturen te bevestigen. Klik op \"Facturen uploaden\"." :
                      tab === "dupe" ? "Geen dubbele facturen gevonden." :
                      tab === "confirmed" ? "Nog geen bevestigde facturen." :
                      "Nog geen facturen. Klik op \"Facturen uploaden\"."}
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      )}

      {tab === "dubpay" && (
      <Card className="shadow-sm" data-testid="dubpay-panel">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Mogelijke <b>dubbele leveranciersbetalingen</b>. Bij <b>"dubbele bankboeking"</b> bestaat naast de betaling ook nog een aparte, niet-gekoppelde BNP/Viva-domiciliëring met hetzelfde bedrag. Klik <b>Los dubbel op</b> om de kunstmatige betaalboeking te verwijderen en de <b>échte</b> bankregel aan de factuur te koppelen. De echte banktransactie wordt nooit verwijderd.</span>
          </div>
          {dubPay === null ? (
            <div className="py-12 text-center text-muted-foreground flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Controleren…</div>
          ) : dubPay.items.length === 0 ? (
            <div className="py-12 text-center text-emerald-700" data-testid="dubpay-empty">Geen dubbele betalingen gevonden. ✓</div>
          ) : (
            <div className="space-y-2" data-testid="dubpay-list">
              {dubPay.items.some((it) => it.flags.includes("dubbele_bankboeking")) && canWrite && (
                <div className="flex items-center justify-between gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2">
                  <span className="text-sm text-red-800">{dubPay.items.filter((it) => it.flags.includes("dubbele_bankboeking")).length} veilige dubbel(s) — kunstmatige boeking wordt verwijderd, echte bankregel gekoppeld.</span>
                  <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white shrink-0" disabled={dubBusy === "__all__"} data-testid="dubpay-resolve-all" onClick={resolveAllDub}>
                    {dubBusy === "__all__" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Alle dubbels oplossen
                  </Button>
                </div>
              )}
              {dubPay.items.map((it) => (
                <div key={it.invoice_id} className="rounded-lg border p-3 space-y-2" data-testid={`dubpay-item-${it.invoice_id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-sm">
                      <span className="font-medium">{it.supplier_name || "Onbekend"}</span>
                      <span className="text-muted-foreground"> · factuur {it.invoice_number || "—"} · {it.invoice_date ? nlDate(it.invoice_date) : "—"}</span>
                      <span className="font-mono text-xs text-muted-foreground block">rekening {it.supplier_account}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {it.flags.includes("dubbele_bankboeking") && <Badge className="bg-red-600 text-white" data-testid={`dubpay-flag-boeking-${it.invoice_id}`}>Dubbele bankboeking</Badge>}
                      {it.flags.includes("overbetaling") && <Badge className="bg-amber-500 text-white" data-testid={`dubpay-flag-over-${it.invoice_id}`}>Overbetaling</Badge>}
                    </div>
                  </div>
                  <div className="text-xs tabular-nums text-muted-foreground">Factuur {eur(it.total)} · betaald <b className="text-foreground">{eur(it.paid_amount)}</b></div>
                  <div className="space-y-0.5">
                    {it.payments.map((p) => (
                      <div key={p.payment_id} className="text-xs flex items-center gap-2">
                        <span className={p.payment_id === it.dup_payment_id ? "text-red-700 font-medium" : "text-muted-foreground"}>{p.date ? nlDate(p.date) : "—"} · {PAY_SRC_LABEL[p.source] || p.source} · {eur(p.amount)}{p.payment_id === it.dup_payment_id ? " · kunstmatige boeking" : ""}</span>
                      </div>
                    ))}
                    {it.bank_entry && (
                      <div className="text-xs text-emerald-700" data-testid={`dubpay-bankentry-${it.invoice_id}`}>
                        Echte bankregel: {it.bank_entry.date ? nlDate(it.bank_entry.date) : "—"} · {(it.bank_entry.source || "").toUpperCase()} · {eur(it.bank_entry.amount)}{it.bank_entry.bewijsstuk ? ` · ${it.bank_entry.bewijsstuk}` : ""}
                      </div>
                    )}
                  </div>
                  {canWrite && it.flags.includes("dubbele_bankboeking") && (
                    <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" disabled={dubBusy === it.invoice_id} data-testid={`dubpay-resolve-${it.invoice_id}`} onClick={() => resolveDub(it)}>
                      {dubBusy === it.invoice_id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Los dubbel op (behoud bankregel)
                    </Button>
                  )}
                  {!it.flags.includes("dubbele_bankboeking") && it.flags.includes("overbetaling") && (
                    <p className="text-[11px] text-muted-foreground">Overbetaling — open de factuur en ontkoppel één betaling indien nodig.</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Review dialog */}
      <Dialog open={!!review} onOpenChange={(o) => !o && setReview(null)}>
        <DialogContent className="max-w-7xl w-[96vw] h-[95vh] max-h-[95vh] overflow-hidden flex flex-col p-4 sm:p-6">
          <DialogHeader className="shrink-0">
            <DialogTitle className="font-heading flex items-center gap-2"><ReceiptText className="h-5 w-5 text-indigo-600" /> Factuur nakijken</DialogTitle>
            <DialogDescription>Links de factuur, rechts de gegevens. Controleer en bevestig.</DialogDescription>
          </DialogHeader>
          {edit && (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 flex-1 min-h-0">
              <div className="lg:col-span-3 min-h-0">
                <InvoicePreview docId={edit.doc_id} filename={edit.filename} />
              </div>
              <div className="lg:col-span-2 space-y-3 lg:overflow-y-auto lg:min-h-0 lg:pr-2 lg:-mr-2">
                {_isDupeEdit && (
                  <div className="rounded-md border-2 border-red-300 bg-red-50 p-3 text-sm text-red-800 flex items-start gap-2 font-medium" data-testid="factuur-dup-warn">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> Dubbele factuur — er bestaat al een factuur met hetzelfde factuurnummer bij deze leverancier. Controleer voordat u boekt.
                  </div>
                )}
                {_mismatch && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 flex items-start gap-2" data-testid="reconcile-warn">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>De btw-lijnen tellen op tot <b>{eur(_linesIncl)}</b>, maar het factuurtotaal is <b>{eur(_docTotal)}</b>. Pas de bedragen hieronder aan zodat ze kloppen.</span>
                  </div>
                )}

                {/* Primair: factuurnummer controleren */}
                <div className="rounded-lg border-2 border-indigo-200 bg-indigo-50/50 p-3 space-y-1.5">
                  <Label className="text-indigo-800">Factuurnummer</Label>
                  <Input className="bg-white text-base font-mono" autoFocus data-testid="edit-invoice-number" placeholder="Vul het factuurnummer in…"
                    value={edit.invoice_number || ""} onChange={(e) => setEdit({ ...edit, invoice_number: e.target.value })} />
                </div>

                {/* Kost / Opbrengst */}
                <div className="rounded-lg border p-3 space-y-2" data-testid="kind-toggle">
                  <div className="flex items-center justify-between">
                    <Label>Soort document</Label>
                    <div className="inline-flex rounded-md border overflow-hidden">
                      <button type="button" data-testid="kind-kost"
                        className={`px-3 py-1.5 text-sm font-medium transition-colors ${edit.kind !== "opbrengst" ? "bg-slate-800 text-white" : "bg-white text-muted-foreground hover:bg-accent"}`}
                        onClick={() => setEdit({ ...edit, kind: "kost", carrier: "" })}>Kost</button>
                      <button type="button" data-testid="kind-opbrengst"
                        className={`px-3 py-1.5 text-sm font-medium transition-colors ${edit.kind === "opbrengst" ? "bg-emerald-600 text-white" : "bg-white text-muted-foreground hover:bg-accent"}`}
                        onClick={() => setEdit({ ...edit, kind: "opbrengst", carrier: edit.carrier || "" })}>Opbrengst</button>
                    </div>
                  </div>
                  {edit.kind === "opbrengst" ? (
                    <div className="space-y-2">
                      <p className="text-[11px] text-emerald-700">Opbrengst van een pakjesbedrijf — de btw wordt als <b>verschuldigd</b> (output) geboekt.</p>
                      <Select value={edit.carrier || ""} onValueChange={(v) => setEdit({ ...edit, carrier: v })}>
                        <SelectTrigger data-testid="edit-carrier"><SelectValue placeholder="Kies pakjesbedrijf" /></SelectTrigger>
                        <SelectContent>{CARRIERS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <p className="text-[11px] text-muted-foreground">Aankoop/kost — de btw is <b>aftrekbaar</b> (terug te vorderen).</p>
                  )}
                </div>

                {edit.category === "autokosten" && edit.kind !== "opbrengst" && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 space-y-2" data-testid="auto-card">
                    <Label className="text-amber-900">Voertuig & kostensoort (autokosten)</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Select value={edit.vehicle || ""} onValueChange={(v) => { const veh = VEHICLES.find((x) => x.label === v); setEdit({ ...edit, vehicle: v, vehicle_plate: veh ? veh.plate : "" }); }}>
                        <SelectTrigger data-testid="edit-vehicle"><SelectValue placeholder="Kies voertuig" /></SelectTrigger>
                        <SelectContent>{VEHICLES.map((x) => <SelectItem key={x.label} value={x.label}>{x.label} ({x.plate})</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={edit.auto_type || ""} onValueChange={(v) => setEdit({ ...edit, auto_type: v })}>
                        <SelectTrigger data-testid="edit-auto-type"><SelectValue placeholder="Kies kostensoort" /></SelectTrigger>
                        <SelectContent>{AUTO_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <p className="text-[11px] text-amber-800/80">Bepaalt de onderverdeling per auto (GLE/Vito) en per soort (leasing/onderhoud) in het overzicht en de export.</p>
                  </div>
                )}

                {edit.category === "nutsvoorzieningen" && edit.kind !== "opbrengst" && (
                  <div className="rounded-lg border border-sky-200 bg-sky-50/50 p-3 space-y-2.5" data-testid="energie-card">
                    <Label className="text-sky-900">Energiefactuur (vestiging, soort & split)</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Select value={edit.location || ""} onValueChange={(v) => setEdit({ ...edit, location: v })}>
                        <SelectTrigger data-testid="edit-location"><SelectValue placeholder="Kies vestiging" /></SelectTrigger>
                        <SelectContent>{LOCATIONS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={edit.energy_type || ""} onValueChange={(v) => setEdit({ ...edit, energy_type: v })}>
                        <SelectTrigger data-testid="edit-energy-type"><SelectValue placeholder="Gas / elektriciteit" /></SelectTrigger>
                        <SelectContent>{ENERGY_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    {/* Voorschot / Eindafrekening */}
                    <div>
                      <Label className="text-[11px] uppercase tracking-wide text-sky-800/80">Soort factuur</Label>
                      <div className="inline-flex rounded-md border border-sky-200 overflow-hidden mt-1" data-testid="bill-type-toggle">
                        {BILL_TYPES.map((b) => (
                          <button key={b.v} type="button" data-testid={`bill-type-${b.v}`}
                            className={`px-3 py-1.5 text-sm font-medium transition-colors ${edit.energy_bill_type === b.v ? "bg-sky-600 text-white" : "bg-white text-muted-foreground hover:bg-accent"}`}
                            onClick={() => setEdit({ ...edit, energy_bill_type: edit.energy_bill_type === b.v ? "" : b.v })}>{b.l}</button>
                        ))}
                      </div>
                    </div>
                    {/* Handmatige split gas / elektriciteit (excl. btw) */}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-[11px] uppercase tracking-wide text-orange-700">Aardgas (excl. btw)</Label>
                        <Input className="h-8 bg-white" type="number" step="0.01" placeholder="0.00" data-testid="edit-gas-amount"
                          value={edit.gas_amount ?? ""} onChange={(e) => setEdit({ ...edit, gas_amount: e.target.value })} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[11px] uppercase tracking-wide text-amber-700">Elektriciteit (excl. btw)</Label>
                        <Input className="h-8 bg-white" type="number" step="0.01" placeholder="0.00" data-testid="edit-electricity-amount"
                          value={edit.electricity_amount ?? ""} onChange={(e) => setEdit({ ...edit, electricity_amount: e.target.value })} />
                      </div>
                    </div>
                    <p className="text-[11px] text-sky-800/80">Kies eerst de vestiging, daarna het bedrag voor aardgas en elektriciteit (excl. btw). De AI stelt een verdeling voor die je kan aanpassen. Bepaalt de splitsing per winkel en per energiesoort in het energieoverzicht.</p>
                  </div>
                )}

                {_showCredit && (
                  <div className="rounded-lg border-2 border-red-200 bg-red-50/50 p-3 space-y-1.5" data-testid="credit-bar">
                    <Label className="text-red-800">Creditnotanummer</Label>
                    <Input className="bg-white text-base font-mono" data-testid="edit-credit-number" placeholder="Vul het creditnotanummer in…"
                      value={edit.credit_note_number || ""} onChange={(e) => setCreditNumber(e.target.value)} />
                    <p className="text-[11px] text-red-700/80">Creditnota herkend — wordt negatief geboekt.</p>
                  </div>
                )}

                <div className="flex justify-end">
                  <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" data-testid="toggle-advanced" onClick={() => setAdvanced((a) => !a)}>
                    {advanced ? "Eenvoudige weergave" : "Alle velden aanpassen"}
                  </Button>
                </div>

                {!advanced ? (
                  <div className="rounded-md border p-3 grid grid-cols-2 gap-x-4 gap-y-3" data-testid="review-simple">
                    <div className="col-span-2"><RO label="Leverancier" value={edit.supplier_name} testid="ro-supplier" /></div>
                    <RO label="BTW-nummer" value={edit.supplier_vat} mono testid="ro-vat" />
                    <RO label="Factuurdatum" value={edit.invoice_date ? nlDate(edit.invoice_date) : ""} testid="ro-date" />
                    <div className="col-span-2"><RO label="Kostencategorie" value={catLabel(edit.category)} testid="ro-category" /></div>
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="review-advanced">
                    <div className="space-y-1.5"><Label>Leveranciersnaam</Label>
                      <Input data-testid="edit-supplier-name" value={edit.supplier_name || ""} onChange={(e) => setEdit({ ...edit, supplier_name: e.target.value })} /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5"><Label>BTW-nummer</Label>
                        <Input data-testid="edit-supplier-vat" value={edit.supplier_vat || ""} onChange={(e) => setEdit({ ...edit, supplier_vat: e.target.value })} /></div>
                      <div className="space-y-1.5"><Label>Factuurdatum</Label>
                        <Input type="date" data-testid="edit-date" value={edit.invoice_date || ""} onChange={(e) => setEdit({ ...edit, invoice_date: e.target.value })} /></div>
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" data-testid="edit-credit-note" checked={!!edit.is_credit_note} onChange={(e) => setEdit({ ...edit, is_credit_note: e.target.checked })} />
                      Creditnota (wordt negatief geboekt)
                    </label>
                    <div className="space-y-1.5"><Label>Kostencategorie</Label>
                      <Select value={edit.category || ""} onValueChange={(v) => setEdit({ ...edit, category: v })}>
                        <SelectTrigger data-testid="edit-category"><SelectValue placeholder="Kies categorie" /></SelectTrigger>
                        <SelectContent>{categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <div className="flex items-center gap-2 pt-1">
                        <Input className="h-8" placeholder="Nieuwe categorie…" data-testid="edit-new-cat" value={newCat} onChange={(e) => setNewCat(e.target.value)} />
                        <Button size="sm" variant="outline" className="h-8" data-testid="edit-add-cat" onClick={addCategory}><Plus className="h-3.5 w-3.5" /></Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* BTW-maatstaven: read-only (automatisch uitgelezen) in eenvoudige weergave;
                    enkel aanpasbaar via "Alle velden aanpassen" voor uitzonderingen. */}
                {!advanced ? (
                  <div className="space-y-1.5">
                    <Label>BTW-maatstaven <span className="text-muted-foreground font-normal">(automatisch uitgelezen)</span></Label>
                    <div className="rounded-md border divide-y" data-testid="vat-readonly">
                      {(edit.vat_lines || []).length > 0 ? (
                        <>
                          <div className="grid grid-cols-4 gap-2 px-3 py-1.5 text-[11px] text-muted-foreground">
                            <span>Tarief</span><span className="text-right">Excl. btw</span><span className="text-right">BTW</span><span className="text-right">Incl. btw</span>
                          </div>
                          {(edit.vat_lines || []).map((vl, i) => (
                            <div key={i} className="grid grid-cols-4 gap-2 px-3 py-1.5 text-sm" data-testid={`vat-ro-${i}`}>
                              <span className="text-muted-foreground">{vl.rate}%</span>
                              <span className="text-right tabular-nums">{eur(parseFloat(vl.base) || 0)}</span>
                              <span className="text-right tabular-nums text-muted-foreground">{eur(parseFloat(vl.vat) || 0)}</span>
                              <span className="text-right tabular-nums">{eur((parseFloat(vl.base) || 0) + (parseFloat(vl.vat) || 0))}</span>
                            </div>
                          ))}
                        </>
                      ) : <p className="px-3 py-2 text-xs text-muted-foreground">Geen btw-maatstaven uitgelezen.</p>}
                      <div className="grid grid-cols-4 gap-2 px-3 py-1.5 text-sm font-semibold bg-slate-50">
                        <span>Totaal</span><span></span><span></span>
                        <span className="text-right tabular-nums" data-testid="vat-ro-total">{eur(parseFloat(edit.total_incl) || 0)}</span>
                      </div>
                    </div>
                    <p className="text-[11px] text-muted-foreground">Nakijken/corrigeren kan via “Alle velden aanpassen”.</p>
                  </div>
                ) : (
                <>
                {/* Bedragen — aanpasbaar (enkel in geavanceerde modus) */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between"><Label>BTW-maatstaven <span className="text-muted-foreground font-normal">({edit.kind === "opbrengst" ? "btw verschuldigd" : "aanpasbaar"})</span></Label>
                    <Button size="sm" variant="ghost" className="h-7" data-testid="edit-add-vat" onClick={addVat}><Plus className="h-3.5 w-3.5 mr-1" /> Regel</Button></div>
                  <div className="space-y-1.5">
                    {edit.vat_lines.length > 0 && (
                      <div className="grid grid-cols-[70px_1fr_1fr_1fr_auto] gap-2 text-[11px] text-muted-foreground px-0.5">
                        <span>Tarief</span><span>Excl. btw</span><span>BTW</span><span>Incl. btw</span><span></span>
                      </div>
                    )}
                    {edit.vat_lines.map((vl, i) => (
                      <div key={i} className="grid grid-cols-[70px_1fr_1fr_1fr_auto] gap-2 items-center" data-testid={`vat-row-${i}`}>
                        <Select value={String(vl.rate)} onValueChange={(v) => setLineRate(i, v)}>
                          <SelectTrigger className="h-8" data-testid={`vat-rate-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{RATES.map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
                        </Select>
                        <Input className="h-8" type="number" step="0.01" placeholder="Excl." data-testid={`vat-base-${i}`} value={vl.base} onChange={(e) => setLineBase(i, e.target.value)} />
                        <Input className="h-8" type="number" step="0.01" placeholder="BTW" data-testid={`vat-vat-${i}`} value={vl.vat} onChange={(e) => setLineVat(i, e.target.value)} />
                        <Input className="h-8" type="number" step="0.01" placeholder="Incl." data-testid={`vat-incl-${i}`} value={r2((parseFloat(vl.base) || 0) + (parseFloat(vl.vat) || 0))} onChange={(e) => setLineIncl(i, e.target.value)} />
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => removeVat(i)}><X className="h-3.5 w-3.5" /></Button>
                      </div>
                    ))}
                    {edit.vat_lines.length === 0 && <p className="text-xs text-muted-foreground">Geen btw-maatstaven. Voeg toe voor handelsgoederen (0/6/21%).</p>}
                  </div>
                </div>
                <div className="space-y-1.5"><Label>Totaal (incl. btw)</Label>
                  <Input type="number" step="0.01" data-testid="edit-total" value={edit.total_incl} onChange={(e) => setEdit({ ...edit, total_incl: e.target.value })} />
                  {edit.vat_lines.length > 0 && <p className="text-[11px] text-muted-foreground">Automatisch berekend uit de btw-lijnen (Excl. + BTW).</p>}
                </div>
                </>
                )}

                {edit.kind !== "opbrengst" && (
                  <label className="flex items-start gap-2.5 rounded-lg border p-3 cursor-pointer hover:bg-accent/40" data-testid="edit-bedrijfsmiddel-label">
                    <input type="checkbox" className="mt-0.5 h-4 w-4" data-testid="edit-bedrijfsmiddel"
                      checked={!!edit.is_bedrijfsmiddel} onChange={(e) => setEdit({ ...edit, is_bedrijfsmiddel: e.target.checked })} />
                    <span className="text-sm leading-snug">
                      <span className="font-medium">Dit is een bedrijfsmiddel (investering)</span>
                      <span className="block text-xs text-muted-foreground">Boekt op rekening 24 en komt in <b>rooster 83</b> van de btw-aangifte i.p.v. 81/82.</span>
                    </span>
                  </label>
                )}

                {edit.kind !== "opbrengst" && edit.is_bedrijfsmiddel && (
                  <div className="space-y-2.5 rounded-lg border border-violet-200 bg-violet-50/60 p-3" data-testid="asset-fields">
                    <div className="text-xs font-medium text-violet-900">Afschrijven als vast actief (er wordt automatisch een vast actief aangemaakt bij bevestigen)</div>
                    <div className="grid grid-cols-2 gap-2.5">
                      <div className="space-y-1">
                        <Label className="text-xs">Categorie</Label>
                        <select className="w-full h-9 rounded-md border px-2 text-sm bg-background" data-testid="edit-asset-category"
                          value={edit.asset_category || ""}
                          onChange={(e) => {
                            const terms = { it_hardware: 3, rollend_materieel: 5, meubilair_inrichting: 10, machines_installaties: 5, gebouwen: 33 };
                            setEdit({ ...edit, asset_category: e.target.value, asset_term_years: edit.asset_term_years || terms[e.target.value] || "" });
                          }}>
                          <option value="">Kies…</option>
                          <option value="it_hardware">IT / hardware (3j)</option>
                          <option value="rollend_materieel">Rollend materieel (5j)</option>
                          <option value="meubilair_inrichting">Meubilair &amp; inrichting (10j)</option>
                          <option value="machines_installaties">Machines / installaties (5j)</option>
                          <option value="gebouwen">Gebouwen (33j)</option>
                        </select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Termijn (jaren)</Label>
                        <Input type="number" min="1" className="h-9" data-testid="edit-asset-term"
                          value={edit.asset_term_years || ""} onChange={(e) => setEdit({ ...edit, asset_term_years: e.target.value })} placeholder="bv. 5" />
                      </div>
                    </div>
                  </div>
                )}

                {_showDiscount && (
                  <div className="space-y-1.5 rounded-lg border border-amber-200 bg-amber-50/60 p-3" data-testid="korting-card">
                    <Label className="text-amber-900">Ontvangen commerciële korting (608000)</Label>
                    <Input type="number" step="0.01" data-testid="edit-commercial-discount"
                      value={edit.commercial_discount ?? 0}
                      onChange={(e) => setEdit({ ...edit, commercial_discount: e.target.value })} />
                    <p className="text-[11px] text-amber-800">Afzonderlijk vermelde leverancierskorting (buiten btw). Handelsgoederen worden bruto op 604000 geboekt, de korting als credit op <b>608000</b>.</p>
                  </div>
                )}

                {/* Grootboekvoorstel — dubbele boeking, bevestig samen met de factuur */}
                <div className="space-y-1.5 rounded-lg border border-indigo-100 bg-indigo-50/40 p-3" data-testid="grootboek-voorstel">
                  {_isClb ? (
                    <>
                      <Label className="text-indigo-900">Grootboek — CLB loonboeking</Label>
                      <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="clb-paid-label">
                        <input type="checkbox" className="h-4 w-4" data-testid="clb-paid"
                          checked={!!edit.clb_paid} onChange={(e) => setClbPaid(e.target.checked)} />
                        <span>Deze factuur is al betaald — toon ook <b>blok C</b> (betaling via 550000)</span>
                      </label>
                      {_clbBlocks.map((blk, bi) => {
                        const bd = r2(blk.lines.reduce((s, l) => s + (parseFloat(l.debet) || 0), 0));
                        const bc = r2(blk.lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0));
                        return (
                          <div key={bi} className="space-y-1 pt-2 border-t first:border-t-0" data-testid={`clb-block-${bi}`}>
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-semibold text-indigo-900">{blk.title}</span>
                              {bd === bc
                                ? <Badge variant="outline" className="text-emerald-700 border-emerald-300" data-testid={`clb-balans-ok-${bi}`}>In balans</Badge>
                                : <Badge variant="outline" className="text-red-700 border-red-300" data-testid={`clb-balans-nok-${bi}`}>Niet in balans</Badge>}
                            </div>
                            <div className="text-[11px] text-indigo-800/80" data-testid={`clb-stuk-${bi}`}>
                              Verantwoordingsstuk: <b>{blk.factuurnummer || edit.invoice_number || "—"}</b> · {blk.factuurdatum || edit.invoice_date || "—"}
                            </div>
                            <div className="grid grid-cols-[80px_1fr_84px_84px_24px] gap-1.5 text-[11px] text-muted-foreground px-0.5">
                              <span>Rekening</span><span>Omschrijving</span><span className="text-right">Debet</span><span className="text-right">Credit</span><span></span>
                            </div>
                            {blk.lines.map((l, i) => (
                              <div key={i} className="grid grid-cols-[80px_1fr_84px_84px_24px] gap-1.5 items-center" data-testid={`clb-line-${bi}-${i}`}>
                                <Input className="h-8 font-mono text-xs px-1.5" list="mar-accounts" placeholder="bv. 618000" data-testid={`clb-acc-${bi}-${i}`}
                                  value={l.account} onChange={(e) => setBlockLine(bi, i, "account", e.target.value)} />
                                <Input className="h-8 text-sm px-1.5" data-testid={`clb-name-${bi}-${i}`}
                                  value={l.name} onChange={(e) => setBlockLine(bi, i, "name", e.target.value)} />
                                <Input className="h-8 text-sm text-right px-1.5 tabular-nums" type="number" step="0.01" data-testid={`clb-deb-${bi}-${i}`}
                                  value={l.debet} onChange={(e) => setBlockLine(bi, i, "debet", e.target.value)} />
                                <Input className="h-8 text-sm text-right px-1.5 tabular-nums" type="number" step="0.01" data-testid={`clb-cred-${bi}-${i}`}
                                  value={l.credit} onChange={(e) => setBlockLine(bi, i, "credit", e.target.value)} />
                                <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`clb-del-${bi}-${i}`} onClick={() => removeBlockLine(bi, i)}><X className="h-3.5 w-3.5" /></Button>
                              </div>
                            ))}
                            <div className="flex items-center justify-between pt-1">
                              <Button size="sm" variant="ghost" className="h-7 text-xs text-indigo-700" data-testid={`clb-add-${bi}`} onClick={() => addBlockLine(bi)}><Plus className="h-3.5 w-3.5 mr-1" /> Regel</Button>
                              <div className="grid grid-cols-[1fr_84px_84px_24px] gap-1.5 text-sm font-semibold flex-1 ml-2">
                                <span className="text-right">Totaal</span>
                                <span className="text-right tabular-nums">{eur(bd)}</span>
                                <span className="text-right tabular-nums">{eur(bc)}</span>
                                <span></span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <p className="text-[11px] text-muted-foreground">Rekeningen en bedragen zijn aanpasbaar. Debet vóór credit. Bevestig samen met de factuur (blok A + B{edit.clb_paid ? " + C" : ""}).</p>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center justify-between">
                        <Label className="text-indigo-900">Grootboek — {edit.grootboek_override && edit.grootboek_override.length ? "aangepast" : "voorstel"}</Label>
                        <div className="flex items-center gap-2">
                          {(gbEdit ? _gbEditDeb === _gbEditCred : _gbDeb === _gbCred)
                            ? <Badge variant="outline" className="text-emerald-700 border-emerald-300" data-testid="grootboek-balans-ok">In balans</Badge>
                            : <Badge variant="outline" className="text-red-700 border-red-300" data-testid="grootboek-balans-nok">Niet in balans</Badge>}
                          {canWrite && !gbEdit && (
                            <Button size="sm" variant="outline" className="h-7 px-2 text-xs" data-testid="gb-edit-start" onClick={startGbEdit}>Aanpassen</Button>
                          )}
                        </div>
                      </div>
                      <div className="text-[11px] text-indigo-800/80" data-testid="grootboek-stuk">
                        Verantwoordingsstuk: <b>{edit.invoice_number || edit.credit_note_number || edit.document_number || "—"}</b> · {edit.invoice_date || "—"}
                      </div>
                      {gbEdit ? (
                        <div className="space-y-1.5" data-testid="gb-edit">
                          <div className="grid grid-cols-[72px_1fr_84px_84px_24px] gap-1.5 text-[11px] text-muted-foreground px-0.5">
                            <span>Rekening</span><span>Omschrijving</span><span className="text-right">Debet</span><span className="text-right">Credit</span><span></span>
                          </div>
                          {gbEdit.map((l, i) => (
                            <div key={i} className="grid grid-cols-[72px_1fr_84px_84px_24px] gap-1.5 items-center" data-testid={`gb-edit-line-${i}`}>
                              <Input className="h-8 font-mono text-xs px-1.5" list="mar-accounts" placeholder="bv. 604006" data-testid={`gb-acct-${i}`} value={l.account} onChange={(e) => setGbLine(i, "account", e.target.value)} />
                              <Input className="h-8 text-xs px-1.5" data-testid={`gb-name-${i}`} value={l.name} onChange={(e) => setGbLine(i, "name", e.target.value)} />
                              <Input type="number" step="0.01" className="h-8 text-xs px-1.5 text-right" data-testid={`gb-deb-${i}`} value={l.debet || ""} onChange={(e) => setGbLine(i, "debet", e.target.value)} />
                              <Input type="number" step="0.01" className="h-8 text-xs px-1.5 text-right" data-testid={`gb-cred-${i}`} value={l.credit || ""} onChange={(e) => setGbLine(i, "credit", e.target.value)} />
                              <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`gb-del-${i}`} onClick={() => removeGbLine(i)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                            </div>
                          ))}
                          <div className="grid grid-cols-[72px_1fr_84px_84px_24px] gap-1.5 text-sm font-semibold border-t pt-1">
                            <span></span><span>Totaal</span>
                            <span className="text-right tabular-nums" data-testid="gb-edit-tot-debet">{eur(_gbEditDeb)}</span>
                            <span className="text-right tabular-nums" data-testid="gb-edit-tot-credit">{eur(_gbEditCred)}</span><span></span>
                          </div>
                          <div className="flex items-center gap-2 pt-1">
                            <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="gb-add-line" onClick={addGbLine}>+ Regel</Button>
                            <Button size="sm" className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700 ml-auto" disabled={_gbEditDeb !== _gbEditCred} data-testid="gb-save" onClick={saveGbOverride}>Opslaan</Button>
                            <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid="gb-cancel" onClick={cancelGbEdit}>Annuleren</Button>
                          </div>
                          {_gbEditDeb !== _gbEditCred && <p className="text-[11px] text-red-600">Debet en credit moeten gelijk zijn om op te slaan.</p>}
                        </div>
                      ) : _grootboek.length === 0 ? (
                        <p className="text-xs text-muted-foreground">Voeg btw-maatstaven toe of pas de boeking handmatig aan.</p>
                      ) : (
                        <div className="space-y-1">
                          <div className="grid grid-cols-[64px_1fr_92px_92px] gap-2 text-[11px] text-muted-foreground px-0.5">
                            <span>Rekening</span><span>Omschrijving</span><span className="text-right">Debet</span><span className="text-right">Credit</span>
                          </div>
                          {_grootboek.map((l, i) => (
                            <div key={i} className="grid grid-cols-[64px_1fr_92px_92px] gap-2 text-sm items-center" data-testid={`grootboek-line-${i}`}>
                              <span className="font-mono text-xs">{l.account}</span>
                              <span className="truncate">{l.name}</span>
                              <span className="text-right tabular-nums">{l.debet ? eur(l.debet) : ""}</span>
                              <span className="text-right tabular-nums">{l.credit ? eur(l.credit) : ""}</span>
                            </div>
                          ))}
                          <div className="grid grid-cols-[64px_1fr_92px_92px] gap-2 text-sm font-semibold border-t pt-1 mt-1">
                            <span></span><span>Totaal</span>
                            <span className="text-right tabular-nums" data-testid="grootboek-tot-debet">{eur(_gbDeb)}</span>
                            <span className="text-right tabular-nums" data-testid="grootboek-tot-credit">{eur(_gbCred)}</span>
                          </div>
                        </div>
                      )}
                      {edit.grootboek_override && edit.grootboek_override.length && !gbEdit ? (
                        <button className="text-[11px] text-indigo-600 underline" data-testid="gb-reset" onClick={resetGbOverride}>Terug naar automatisch voorstel</button>
                      ) : null}
                      <p className="text-[11px] text-muted-foreground">Wordt mee geboekt bij het bevestigen van deze factuur.</p>
                    </>
                  )}
                </div>

                {advanced ? (
                  <div className="space-y-1.5"><Label>Opmerking</Label>
                    <Textarea rows={2} data-testid="edit-note" value={edit.note || ""} onChange={(e) => setEdit({ ...edit, note: e.target.value })} /></div>
                ) : (edit.note && <RO label="Opmerking" value={edit.note} testid="ro-note" />)}

                {edit.kind !== "opbrengst" && (
                  <div className="rounded-md border p-3 space-y-2.5" data-testid="pay-section">
                    <div className="flex items-center justify-between">
                      <Label className="text-indigo-900">Betaling</Label>
                      {edit.paid
                        ? <Badge className="bg-emerald-600 text-white" data-testid="pay-status-paid">Betaald ✓</Badge>
                        : (edit.paid_amount > 0
                          ? <Badge className="bg-amber-500 text-white" data-testid="pay-status-partial">Deels betaald</Badge>
                          : <Badge variant="outline" className="text-amber-700 border-amber-300" data-testid="pay-status-unpaid">Niet betaald</Badge>)}
                    </div>

                    {edit.payments && edit.payments.length > 0 && (
                      <div className="space-y-1" data-testid="pay-list">
                        {edit.payments.map((p) => (
                          <div key={p.payment_id} className="flex items-center justify-between gap-2 rounded border bg-white px-2 py-1 text-xs" data-testid={`pay-item-${p.payment_id}`}>
                            <span className="truncate">{p.date ? nlDate(p.date) : "—"} · {PAY_SRC_LABEL[p.source] || p.source} · <b className="tabular-nums">{eur(p.amount)}</b>{p.ref ? <span className="text-muted-foreground"> · {p.ref}</span> : null}</span>
                            {canWrite && (
                              <button className="text-amber-700 hover:underline shrink-0" disabled={payBusy} data-testid={`pay-unlink-${p.payment_id}`} onClick={() => markUnpaid(p.payment_id)}>ontkoppel</button>
                            )}
                          </div>
                        ))}
                        <div className="text-xs tabular-nums pt-0.5" data-testid="pay-progress">
                          Betaald <b>{eur(edit.paid_amount || 0)}</b> van <b>{eur(Math.abs(edit.signed_total ?? edit.total_incl ?? 0))}</b>
                          {!edit.paid && <> · <span className="text-amber-700 font-medium">nog {eur(edit.open_amount != null ? edit.open_amount : Math.max(Math.abs(edit.signed_total ?? edit.total_incl ?? 0) - (edit.paid_amount || 0), 0))} open</span></>}
                        </div>
                      </div>
                    )}

                    {edit.paid ? (
                      canWrite && (
                        <Button size="sm" variant="outline" className="text-amber-700 border-amber-300" disabled={payBusy} data-testid="pay-unlink" onClick={() => markUnpaid()}>
                          Alle betalingen ontkoppelen
                        </Button>
                      )
                    ) : (
                      <div className="space-y-2">
                        <p className="text-[11px] text-muted-foreground">{edit.paid_amount > 0 ? "Voeg extra (deel)betalingen toe tot de factuur volledig betaald is." : "Zoek de betaling(en) en koppel ze. Meerdere schijven (ook kaart + cash) mogen."}</p>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {[["bnp", "BNP"], ["viva", "Viva"], ["kasboek", "Kasboek"]].map(([k, lbl]) => (
                            <Button key={k} size="sm" variant={matchSrc === k ? "default" : "outline"} className={matchSrc === k ? "bg-indigo-600 hover:bg-indigo-700" : ""} disabled={matchLoading} data-testid={`pay-src-${k}`} onClick={() => searchBank(k)}>
                              Zoek in {lbl}
                            </Button>
                          ))}
                        </div>
                        {matchLoading && <div className="text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Zoeken…</div>}
                        {matchCands && !matchLoading && (
                          matchCands.length === 0 ? (
                            <div className="text-xs text-muted-foreground" data-testid="pay-no-match">Geen verrichting gevonden in {PAY_SRC_LABEL[matchSrc]}.</div>
                          ) : (
                            <>
                            <div className="space-y-1.5" data-testid="pay-candidates">
                              {matchCands.map((c) => {
                                const betaal = Math.abs(parseFloat(c.amount) || 0);
                                const fact = Math.abs(parseFloat(c.invoice_total) || 0);
                                const amtOk = fact > 0 && Math.abs(betaal - fact) <= 0.02;
                                const checked = matchSel.has(c.id);
                                return (
                                <div key={c.id} className={`flex items-center gap-2 rounded border px-2 py-1.5 text-xs ${checked ? "ring-2 ring-indigo-400 bg-indigo-50/40" : ""}`} data-testid={`pay-cand-${c.id}`}>
                                  {canWrite && (
                                    <input type="checkbox" className="h-4 w-4 shrink-0" checked={checked} data-testid={`pay-check-${c.id}`}
                                      onChange={() => setMatchSel((s) => { const n = new Set(s); n.has(c.id) ? n.delete(c.id) : n.add(c.id); return n; })} />
                                  )}
                                  <div className="min-w-0 flex-1">
                                    <div className="truncate font-medium">{c.description || "—"}</div>
                                    {c.mededeling ? <div className="truncate text-muted-foreground" data-testid={`pay-cand-med-${c.id}`}>Mededeling: {c.mededeling}</div> : null}
                                    <div className="text-muted-foreground">{c.date ? nlDate(c.date) : "—"}{c.volgnummer ? ` · volgnr. ${c.volgnummer}` : ""}</div>
                                    <div className="tabular-nums">
                                      Factuur <b>{eur(fact)}</b> · Betaling <b>{eur(betaal)}</b>{" "}
                                      <span className={amtOk ? "text-emerald-600" : "text-amber-600"}>{amtOk ? "✓ zelfde bedrag" : `≠ verschil ${eur(Math.round((betaal - fact) * 100) / 100)}`}</span>
                                    </div>
                                    <div className="text-[10px] text-indigo-600">match: {(c.reasons || []).join(", ")}</div>
                                  </div>
                                  {canWrite && (
                                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 shrink-0" disabled={payBusy} data-testid={`pay-link-${c.id}`} onClick={() => markPaid(matchSrc, c.id)}>
                                      Koppel
                                    </Button>
                                  )}
                                </div>
                                );
                              })}
                            </div>
                            {canWrite && matchSel.size > 0 && (
                              <div className="flex items-center justify-between gap-2 rounded-md bg-indigo-50 border border-indigo-200 px-2.5 py-2">
                                <span className="text-xs tabular-nums" data-testid="pay-sel-total">Geselecteerd <b>{eur([...matchSel].reduce((s, id) => s + Math.abs(parseFloat((matchCands.find((x) => x.id === id) || {}).amount) || 0), 0))}</b> / factuur {eur(Math.abs(edit.signed_total ?? edit.total_incl ?? 0))}</span>
                                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" disabled={payBusy} data-testid="pay-link-selected" onClick={linkSelected}>
                                  Koppel geselecteerde ({matchSel.size})
                                </Button>
                              </div>
                            )}
                            </>
                          )
                        )}
                        {canWrite && (
                          <Button size="sm" variant="ghost" className="text-muted-foreground" disabled={payBusy} data-testid="pay-manual" onClick={() => markPaid("manueel", null)}>
                            {edit.paid_amount > 0 ? "Markeer restsaldo als betaald zonder koppeling" : "Markeer als betaald zonder koppeling"}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 shrink-0 pt-2 border-t mt-1">
            {edit && <Button variant="ghost" className="text-red-600 mr-auto" data-testid="review-delete" onClick={(e) => del(edit, e)}><Trash2 className="h-4 w-4 mr-2" /> Verwijderen</Button>}
            <Button variant="outline" disabled={saving} data-testid="review-save" onClick={saveOnly}>Opslaan</Button>
            {review?.status !== "confirmed" && (
              <Button className="bg-indigo-600 hover:bg-indigo-700" disabled={saving} data-testid="review-confirm" onClick={confirm}>
                <CheckCircle2 className="h-4 w-4 mr-2" /> {saving ? "Bezig…" : "Bevestigen"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
